import { CauseTypeId as CauseTypeId$1, RuntimeExceptionTypeId as RuntimeExceptionTypeId$1, InterruptedExceptionTypeId as InterruptedExceptionTypeId$1, IllegalArgumentExceptionTypeId as IllegalArgumentExceptionTypeId$1, NoSuchElementExceptionTypeId as NoSuchElementExceptionTypeId$1, InvalidPubSubCapacityExceptionTypeId as InvalidPubSubCapacityExceptionTypeId$1, empty as empty$1, fail as fail$1, die as die$1, interrupt as interrupt$1, parallel as parallel$1, sequential as sequential$1, isCause as isCause$1, isEmptyType as isEmptyType$1, isFailType as isFailType$1, isDieType as isDieType$1, isInterruptType as isInterruptType$1, isSequentialType as isSequentialType$1, isParallelType as isParallelType$1, size as size$1, isEmpty as isEmpty$1, isFailure as isFailure$1, isDie as isDie$1, isInterrupted as isInterrupted$1, isInterruptedOnly as isInterruptedOnly$1, failures as failures$1, defects as defects$1, interruptors as interruptors$1, failureOption as failureOption$1, failureOrCause as failureOrCause$1, flipCauseOption as flipCauseOption$1, dieOption as dieOption$1, interruptOption as interruptOption$1, keepDefects as keepDefects$1, linearize as linearize$1, stripFailures as stripFailures$1, stripSomeDefects as stripSomeDefects$1, as as as$1, map as map$1, flatMap as flatMap$1, flatten as flatten$1, contains as contains$1, squash as squash$1, squashWith as squashWith$1, find as find$1, filter as filter$1, match as match$1, reduce as reduce$1, reduceWithContext as reduceWithContext$1, InterruptedException as InterruptedException$1, isInterruptedException as isInterruptedException$1, IllegalArgumentException as IllegalArgumentException$1, isIllegalArgumentException as isIllegalArgumentException$1, NoSuchElementException as NoSuchElementException$1, isNoSuchElementException as isNoSuchElementException$1, RuntimeException as RuntimeException$1, isRuntimeException as isRuntimeException$1, pretty as pretty$1 } from '../../internal/cause.esm.js';
import { originalInstance } from '../../internal/core.esm.js';

/**
 * The `Effect<R, E, A>` type is polymorphic in values of type `E` and we can
 * work with any error type that we want. However, there is a lot of information
 * that is not inside an arbitrary `E` value. So as a result, an `Effect` needs
 * somewhere to store things like unexpected errors or defects, stack and
 * execution traces, causes of fiber interruptions, and so forth.
 *
 * Effect-TS is very strict about preserving the full information related to a
 * failure. It captures all type of errors into the `Cause` data type. `Effect`
 * uses the `Cause<E>` data type to store the full story of failure. So its
 * error model is lossless. It doesn't throw information related to the failure
 * result. So we can figure out exactly what happened during the operation of
 * our effects.
 *
 * It is important to note that `Cause` is an underlying data type representing
 * errors occuring within an `Effect` workflow. Thus, we don't usually deal with
 * `Cause`s directly. Even though it is not a data type that we deal with very
 * often, the `Cause` of a failing `Effect` workflow can be accessed at any
 * time, which gives us total access to all parallel and sequential errors in
 * occurring within our codebase.
 *
 * @since 2.0.0
 */
/**
 * @since 2.0.0
 * @category symbols
 */
const CauseTypeId = CauseTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category symbols
 */
const RuntimeExceptionTypeId = RuntimeExceptionTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category symbols
 */
const InterruptedExceptionTypeId = InterruptedExceptionTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category symbols
 */
const IllegalArgumentExceptionTypeId = IllegalArgumentExceptionTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category symbols
 */
const NoSuchElementExceptionTypeId = NoSuchElementExceptionTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * @since 2.0.0
 * @category symbols
 */
const InvalidPubSubCapacityExceptionTypeId = InvalidPubSubCapacityExceptionTypeId$1;

/**
 * @since 2.0.0
 * @category symbols
 */

/**
 * A `Cause` represents the full history of a failure resulting from running an
 * `Effect` workflow.
 *
 * Effect-TS uses a data structure from functional programming called a semiring
 * to represent the `Cause` data type. This allows us to take a base type `E`
 * (which represents the error type of an `Effect`) and capture the sequential
 * and parallel composition of errors in a fully lossless fashion.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * @since 2.0.0
 */

/**
 * Represents a set of methods that can be used to reduce a `Cause<E>` to a
 * specified value of type `Z` with access to a context of type `C`.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Represents a generic checked exception which occurs at runtime.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Represents a checked exception which occurs when a `Fiber` is interrupted.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Represents a checked exception which occurs when an invalid argument is
 * provided to a method.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Represents a checked exception which occurs when an expected element was
 * unable to be found.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Represents a checked exception which occurs when attempting to construct a
 * `PubSub` with an invalid capacity.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * The `Empty` cause represents a lack of errors.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * The `Fail` cause represents a `Cause` which failed with an expected error of
 * type `E`.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * The `Die` cause represents a `Cause` which failed as a result of a defect, or
 * in other words, an unexpected error.
 *
 * type `E`.
 * @since 2.0.0
 * @category models
 */

/**
 * The `Interrupt` cause represents failure due to `Fiber` interruption, which
 * contains the `FiberId` of the interrupted `Fiber`.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * The `Parallel` cause represents the composition of two causes which occurred
 * in parallel.
 *
 * In Effect-TS programs, it is possible that two operations may be performed in
 * parallel. In these cases, the `Effect` workflow can fail for more than one
 * reason. If both computations fail, then there are actually two errors which
 * occurred in parallel. In these cases, the errors can be represented by the
 * `Parallel` cause.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * The `Sequential` cause represents the composition of two causes which occurred
 * sequentially.
 *
 * For example, if we perform Effect-TS's analog of `try-finally` (i.e.
 * `Effect.ensuring`), and both the `try` and `finally` blocks fail, we have two
 * errors which occurred sequentially. In these cases, the errors can be
 * represented by the `Sequential` cause.
 *
 * @since 2.0.0
 * @category models
 */

/**
 * Constructs a new `Empty` cause.
 *
 * @since 2.0.0
 * @category constructors
 */
const empty = empty$1;

/**
 * Constructs a new `Fail` cause from the specified `error`.
 *
 * @since 2.0.0
 * @category constructors
 */
const fail = fail$1;

/**
 * Constructs a new `Die` cause from the specified `defect`.
 *
 * @since 2.0.0
 * @category constructors
 */
const die = die$1;

/**
 * Constructs a new `Interrupt` cause from the specified `fiberId`.
 *
 * @since 2.0.0
 * @category constructors
 */
const interrupt = interrupt$1;

/**
 * Constructs a new `Parallel` cause from the specified `left` and `right`
 * causes.
 *
 * @since 2.0.0
 * @category constructors
 */
const parallel = parallel$1;

/**
 * Constructs a new `Sequential` cause from the specified pecified `left` and
 * `right` causes.
 *
 * @since 2.0.0
 * @category constructors
 */
const sequential = sequential$1;

/**
 * Returns `true` if the specified value is a `Cause`, `false` otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isCause = isCause$1;

/**
 * Returns `true` if the specified `Cause` is an `Empty` type, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isEmptyType = isEmptyType$1;

/**
 * Returns `true` if the specified `Cause` is a `Fail` type, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isFailType = isFailType$1;

/**
 * Returns `true` if the specified `Cause` is a `Die` type, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isDieType = isDieType$1;

/**
 * Returns `true` if the specified `Cause` is an `Interrupt` type, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isInterruptType = isInterruptType$1;

/**
 * Returns `true` if the specified `Cause` is a `Sequential` type, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isSequentialType = isSequentialType$1;

/**
 * Returns `true` if the specified `Cause` is a `Parallel` type, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isParallelType = isParallelType$1;

/**
 * Returns the size of the cause, calculated as the number of individual `Cause`
 * nodes found in the `Cause` semiring structure.
 *
 * @since 2.0.0
 * @category getters
 */
const size = size$1;

/**
 * Returns `true` if the specified cause is empty, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isEmpty = isEmpty$1;

/**
 * Returns `true` if the specified cause contains a failure, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isFailure = isFailure$1;

/**
 * Returns `true` if the specified cause contains a defect, `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isDie = isDie$1;

/**
 * Returns `true` if the specified cause contains an interruption, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isInterrupted = isInterrupted$1;

/**
 * Returns `true` if the specified cause contains only interruptions (without
 * any `Die` or `Fail` causes), `false` otherwise.
 *
 * @since 2.0.0
 * @category getters
 */
const isInterruptedOnly = isInterruptedOnly$1;

/**
 * Returns a `List` of all recoverable errors of type `E` in the specified
 * cause.
 *
 * @since 2.0.0
 * @category getters
 */
const failures = failures$1;

/**
 * Returns a `List` of all unrecoverable defects in the specified cause.
 *
 * @since 2.0.0
 * @category getters
 */
const defects = defects$1;

/**
 * Returns a `HashSet` of `FiberId`s for all fibers that interrupted the fiber
 * described by the specified cause.
 *
 * @since 2.0.0
 * @category getters
 */
const interruptors = interruptors$1;

/**
 * Returns the `E` associated with the first `Fail` in this `Cause`, if one
 * exists.
 *
 * @since 2.0.0
 * @category getters
 */
const failureOption = failureOption$1;

/**
 * Returns the first checked error on the `Left` if available, if there are
 * no checked errors return the rest of the `Cause` that is known to contain
 * only `Die` or `Interrupt` causes.
 *
 * @since 2.0.0
 * @category getters
 */
const failureOrCause = failureOrCause$1;

/**
 * Converts the specified `Cause<Option<E>>` to an `Option<Cause<E>>` by
 * recursively stripping out any failures with the error `None`.
 *
 * @since 2.0.0
 * @category getters
 */
const flipCauseOption = flipCauseOption$1;

/**
 * Returns the defect associated with the first `Die` in this `Cause`, if one
 * exists.
 *
 * @since 2.0.0
 * @category getters
 */
const dieOption = dieOption$1;

/**
 * Returns the `FiberId` associated with the first `Interrupt` in the specified
 * cause, if one exists.
 *
 * @since 2.0.0
 * @category getters
 */
const interruptOption = interruptOption$1;

/**
 * Remove all `Fail` and `Interrupt` nodes from the specified cause, and return
 * a cause containing only `Die` cause/finalizer defects.
 *
 * @since 2.0.0
 * @category getters
 */
const keepDefects = keepDefects$1;

/**
 * Linearizes the specified cause into a `HashSet` of parallel causes where each
 * parallel cause contains a linear sequence of failures.
 *
 * @since 2.0.0
 * @category getters
 */
const linearize = linearize$1;

/**
 * Remove all `Fail` and `Interrupt` nodes from the specified cause, and return
 * a cause containing only `Die` cause/finalizer defects.
 *
 * @since 2.0.0
 * @category getters
 */
const stripFailures = stripFailures$1;

/**
 * Remove all `Die` causes that the specified partial function is defined at,
 * returning `Some` with the remaining causes or `None` if there are no
 * remaining causes.
 *
 * @since 2.0.0
 * @category getters
 */
const stripSomeDefects = stripSomeDefects$1;

/**
 * @since 2.0.0
 * @category mapping
 */
const as = as$1;

/**
 * @since 2.0.0
 * @category mapping
 */
const map = map$1;

/**
 * @since 2.0.0
 * @category sequencing
 */
const flatMap = flatMap$1;

/**
 * @since 2.0.0
 * @category sequencing
 */
const flatten = flatten$1;

/**
 * Returns `true` if the `self` cause contains or is equal to `that` cause,
 * `false` otherwise.
 *
 * @since 2.0.0
 * @category elements
 */
const contains = contains$1;

/**
 * Squashes a `Cause` down to a single defect, chosen to be the "most important"
 * defect.
 *
 * @since 2.0.0
 * @category destructors
 */
const squash = squash$1;

/**
 * Squashes a `Cause` down to a single defect, chosen to be the "most important"
 * defect. If a recoverable error is found, the provided function will be used
 * to map the error a defect, and the resulting value will be returned.
 *
 * @since 2.0.0
 * @category destructors
 */
const squashWith = squashWith$1;

/**
 * Uses the provided partial function to search the specified cause and attempt
 * to extract information from it.
 *
 * @since 2.0.0
 * @category elements
 */
const find = find$1;

/**
 * Filters causes which match the provided predicate out of the specified cause.
 *
 * @since 2.0.0
 * @category filtering
 */
const filter = filter$1;

/**
 * Folds the specified cause into a value of type `Z`.
 *
 * @since 2.0.0
 * @category folding
 */
const match = match$1;

/**
 * Reduces the specified cause into a value of type `Z`, beginning with the
 * provided `zero` value.
 *
 * @since 2.0.0
 * @category folding
 */
const reduce = reduce$1;

/**
 * Reduces the specified cause into a value of type `Z` using a `Cause.Reducer`.
 * Also allows for accessing the provided context during reduction.
 *
 * @since 2.0.0
 * @category folding
 */
const reduceWithContext = reduceWithContext$1;

/**
 * Represents a checked exception which occurs when a `Fiber` is interrupted.
 *
 * @since 2.0.0
 * @category errors
 */
const InterruptedException = InterruptedException$1;

/**
 * Returns `true` if the specified value is an `InterruptedException`, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isInterruptedException = isInterruptedException$1;

/**
 * Represents a checked exception which occurs when an invalid argument is
 * provided to a method.
 *
 * @since 2.0.0
 * @category errors
 */
const IllegalArgumentException = IllegalArgumentException$1;

/**
 * Returns `true` if the specified value is an `IllegalArgumentException`, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isIllegalArgumentException = isIllegalArgumentException$1;

/**
 * Represents a checked exception which occurs when an expected element was
 * unable to be found.
 *
 * @since 2.0.0
 * @category errors
 */
const NoSuchElementException = NoSuchElementException$1;

/**
 * Returns `true` if the specified value is an `NoSuchElementException`, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isNoSuchElementException = isNoSuchElementException$1;

/**
 * Represents a generic checked exception which occurs at runtime.
 *
 * @since 2.0.0
 * @category errors
 */
const RuntimeException = RuntimeException$1;

/**
 * Returns `true` if the specified value is an `RuntimeException`, `false`
 * otherwise.
 *
 * @since 2.0.0
 * @category refinements
 */
const isRuntimeException = isRuntimeException$1;

/**
 * Returns the specified `Cause` as a pretty-printed string.
 *
 * @since 2.0.0
 * @category rendering
 */
const pretty = pretty$1;

/**
 * Returns the original, unproxied, instance of a thrown error
 *
 * @since 2.0.0
 * @category errors
 */
const originalError = originalInstance;

export { CauseTypeId, IllegalArgumentException, IllegalArgumentExceptionTypeId, InterruptedException, InterruptedExceptionTypeId, InvalidPubSubCapacityExceptionTypeId, NoSuchElementException, NoSuchElementExceptionTypeId, RuntimeException, RuntimeExceptionTypeId, as, contains, defects, die, dieOption, empty, fail, failureOption, failureOrCause, failures, filter, find, flatMap, flatten, flipCauseOption, interrupt, interruptOption, interruptors, isCause, isDie, isDieType, isEmpty, isEmptyType, isFailType, isFailure, isIllegalArgumentException, isInterruptType, isInterrupted, isInterruptedException, isInterruptedOnly, isNoSuchElementException, isParallelType, isRuntimeException, isSequentialType, keepDefects, linearize, map, match, originalError, parallel, pretty, reduce, reduceWithContext, sequential, size, squash, squashWith, stripFailures, stripSomeDefects };
